import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, RefreshCw, Scale } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Badge, Panel, Row, Table, inputCls } from "../ui";

const toKg = (g) => (g === null || g === undefined || g === "" ? "" : (Number(g) / 1000).toFixed(2));

/** Bulk freight weights. Google needs one on every product, and so does our own postage maths. */
export default function WeightsAdmin() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi
      .get("/admin/catalog/weights")
      .then((r) => {
        setData(r.data);
        setDraft(Object.fromEntries(r.data.rows.map((row) => [row.id, toKg(row.weight_grams)])));
      })
      .catch(() => toast.error("Could not load weights"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const dirty = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((row) => (draft[row.id] ?? "") !== toKg(row.weight_grams) || row.estimated);
  }, [data, draft]);

  const save = async () => {
    setSaving(true);
    try {
      const items = dirty
        .map((row) => {
          const kg = String(draft[row.id] ?? "").trim();
          if (kg === "") return { id: row.id, weight_grams: null };
          const n = parseFloat(kg);
          if (!Number.isFinite(n) || n <= 0) return null;
          return { id: row.id, weight_grams: Math.round(n * 1000) };
        })
        .filter(Boolean);
      const r = await adminApi.put("/admin/catalog/weights", { items });
      toast.success(`${r.data.saved} weights confirmed`, {
        description: "Google's feed picks them up on its next fetch.",
      });
      load();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="weights-admin">
      <AdminHeader
        title="Shipping weights"
        subtitle="Packed weight per item - what Google and the freight quote both run off"
      >
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2" data-testid="weights-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
        <button
          onClick={save}
          disabled={saving || dirty.length === 0}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="weights-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Confirm {dirty.length || ""} weights
        </button>
      </AdminHeader>

      <Panel>
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider gold-text">
          <Scale className="h-4 w-4" /> How this works
        </p>
        <p className="mt-2 text-sm leading-relaxed text-[color:var(--nq-stone)]">
          Every line already has a starting weight worked out from what that kind of part
          normally weighs, boxed and ready to post. Anything still marked{" "}
          <Badge tone="gold">estimate</Badge> is a guess - type the real weight over it (or leave
          it if it looks right) and hit confirm. Weigh it packed, on the scales, in kilograms.
        </p>
        {data && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="weights-counts">
            <Badge tone="green">{data.confirmed} confirmed</Badge>
            <Badge tone="gold">{data.estimated} estimated</Badge>
            {data.missing > 0 && <Badge tone="red">{data.missing} blank</Badge>}
          </div>
        )}
      </Panel>

      {loading ? (
        <Panel>
          <p className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading products...
          </p>
        </Panel>
      ) : (
        <Table head={["Product", "Type", "Weight (kg)", "Status"]}>
          {(data?.rows || []).map((row) => (
            <Row key={row.id} testid="weight-row">
              <td className="p-3">
                <p className="font-medium">{row.title}</p>
                <p className="text-xs text-[color:var(--nq-stone)]">{row.sku}</p>
              </td>
              <td className="p-3 text-xs text-[color:var(--nq-stone)]">{row.product_type}</td>
              <td className="p-3">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft[row.id] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [row.id]: e.target.value }))}
                  className={`${inputCls} max-w-[7rem]`}
                  data-testid={`weight-input-${row.sku || row.id}`}
                />
              </td>
              <td className="p-3">
                {row.estimated ? (
                  <span className="flex flex-col gap-1">
                    <Badge tone="gold">estimate</Badge>
                    <span className="text-[11px] text-[color:var(--nq-stone)]">{row.basis}</span>
                  </span>
                ) : row.weight_grams ? (
                  <Badge tone="green">confirmed</Badge>
                ) : (
                  <Badge tone="red">blank</Badge>
                )}
              </td>
            </Row>
          ))}
        </Table>
      )}
    </div>
  );
}
